import React, { useState, useEffect } from "react";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Loader from "./Loader";
import Search from "../assets/svgIcons/Search.svg";
import DropDown from "../assets/svgIcons/PrimaryDropDown.svg";
import { fetchTransactions } from "../apis/transactionApi";
import { useErrorHandler } from "../utils/ErrorHandler";
import { displayToast } from "../utils/displayToast";
import { useOutsideClick } from "../utils/useOutsideClick";
import "../styles/components/TransactionList.css";
import "../styles/components/PageStyles.css";

const TransactionList = () => {
  const itemsPerPage = 8;
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  
  const filters = [
    {
      title: "status",
      display_name: "Status",
    },
    {
      title: "tran_code",
      display_name: "Transaction Code",
    },
    {
      title: "organisation_id",
      display_name: "Organisation Id",
    },
    {
      title: "name",
      display_name: "Name",
    },
  ];

  const dropdownRef = useOutsideClick(() => {
    setIsDropdownOpen(false);
  });

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setIsDropdownOpen(false);
  };

  const navigate = useNavigate();
  const { handleErrorResponse } = useErrorHandler();

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  useEffect(() => {
    fetchTransactionData();
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTransactionData = async (searchParams = {}) => {
    setIsLoading(true);
    try {
      let query = `?count=${itemsPerPage}&page=${currentPage + 1}`;

      if (searchParams.field && searchParams.value) {
        query += `&${searchParams.field}=${searchParams.value}`;
      }

      const response = await fetchTransactions(query);

      if (response && response.items) {
        setTransactions(response.items);
        if (response.total_count) {
          setPageCount(Math.ceil(response.total_count / itemsPerPage));
        }
      } else if (response && response.description) {
        handleErrorResponse(response.description);
      }
    } catch (error) {
      displayToast(`Error fetching transactions: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    if (!selectedCategory.display_name) {
      displayToast("Category not selected", "error");
    } else {
      fetchTransactionData({
        field: selectedCategory.title,
        value: searchValue,
      });
    }
  };

  const handleCreateNew = () => {
    navigate("/transactions/new");
  };

  const handleCreateMasterTransaction = () => {
    navigate("/transactions/master/new");
  };

  const handleEdit = (transaction) => {
    if (transaction.type === "master") {
      navigate(`/transactions/master/edit/${transaction.id}`, {
        state: { transaction },
      });
    } else {
      navigate(`/transactions/edit/${transaction.id}`, {
        state: { transaction },
      });
    }
  };

  const handleView = (transaction) => {
    if (transaction.type === "master") {
      navigate(`/transactions/master/view/${transaction.id}`, {
        state: { transaction, viewMode: true },
      });
    } else {
      navigate(`/transactions/view/${transaction.id}`, {
        state: { transaction, viewMode: true },
      });
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'active') return 'mfg-status-active';
    if (statusLower === 'inactive') return 'mfg-status-inactive';
    if (statusLower === 'pending') return 'mfg-status-pending';
    return 'mfg-status-info';
  };

  return (
    <div className="mfg-page-container">
      <div className="mfg-page-header">
        <div className="mfg-page-title-section">
          <h2 className="mfg-page-title">
            <span className="mfg-page-title-icon">📝</span>
            Transaction Settings
          </h2>
          <p className="mfg-page-subtitle">Manage transaction configurations and settings</p>
        </div>
        <div className="mfg-header-actions">
          <button
            className="mfg-btn mfg-btn-secondary"
            onClick={handleCreateMasterTransaction}
          >
            <span className="mfg-btn-icon">⚙️</span>
            Create Master Transaction
          </button>
          <button
            className="mfg-btn mfg-btn-primary"
            onClick={handleCreateNew}
          >
            <span className="mfg-btn-icon">➕</span>
            Create New Transaction
          </button>
        </div>
      </div>

      <div className="mfg-card">
        <div className="mfg-card-header">
          <h3 className="mfg-card-title">
            <span className="mfg-card-title-icon">📋</span>
            Transactions List
          </h3>
        </div>
        
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className="mfg-filters">
              <div className="mfg-filter-dropdown" ref={dropdownRef}>
                <div
                  className="mfg-filter-dropdown-btn"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span>{selectedCategory.display_name || "Select Category"}</span>
                  <img src={DropDown} alt="dropdown" className="mfg-filter-dropdown-icon" />
                </div>

                {isDropdownOpen && (
                  <div className="mfg-filter-dropdown-menu">
                    {filters?.map((filter, index) => (
                      <div
                        key={index}
                        className="mfg-filter-dropdown-item"
                        onClick={() => handleCategorySelect(filter)}
                      >
                        {filter?.display_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mfg-search-box">
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  className="mfg-search-input"
                />
                <img
                  src={Search}
                  alt="search"
                  className="mfg-search-icon"
                  onClick={handleSearch}
                />
              </div>
            </div>

            {transactions && transactions.length > 0 ? (
              <div className="mfg-table-wrapper">
                <table className="mfg-table">
                  <thead>
                    <tr>
                      <th>Transaction Code</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td>
                          <strong>{transaction.tran_code}</strong>
                        </td>
                        <td>{transaction.name}</td>
                        <td>
                          <span className="mfg-status-badge mfg-status-info">
                            {transaction.type}
                          </span>
                        </td>
                        <td>
                          <span className={`mfg-status-badge ${getStatusBadgeClass(transaction.status)}`}>
                            {transaction.status}
                          </span>
                        </td>
                        <td>
                          <div className="mfg-table-actions">
                            <button
                              onClick={() => handleView(transaction)}
                              className="mfg-btn mfg-btn-outline mfg-btn-sm"
                            >
                              👁️ <span className="mfg-btn-text">View</span>
                            </button>
                            <button
                              onClick={() => handleEdit(transaction)}
                              className="mfg-btn mfg-btn-warning mfg-btn-sm"
                            >
                              ✏️ <span className="mfg-btn-text">Edit</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mfg-empty-state">
                <div className="mfg-empty-icon">📋</div>
                <h4 className="mfg-empty-title">No Transactions Found</h4>
                <p className="mfg-empty-text">Create a new transaction to get started</p>
              </div>
            )}
          </>
        )}
        
        {pageCount > 0 && !isLoading && (
          <ReactPaginate
            breakLabel="..."
            previousLabel={<ChevronLeft size={20} />}
            nextLabel={<ChevronRight size={20} />}
            pageCount={pageCount}
            forcePage={currentPage}
            onPageChange={handlePageClick}
            breakClassName="pagination-box"
            containerClassName="pagination"
            pageClassName="pagination-box"
            pageLinkClassName="pagination-link"
            previousClassName="pagination-box"
            nextClassName="pagination-box"
            previousLinkClassName="pagination-link"
            nextLinkClassName="pagination-link"
            activeClassName="pagination-active"
            disabledClassName="pagination-disabled"
            breakLinkClassName="pagination-link"
          />
        )}
      </div>
    </div>
  );
};

export default TransactionList;
